import React, { useState, useEffect } from "react";
import {
  setDocData,
  storage,
  queryAllDocsByField,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import toast from "react-hot-toast";
import { v4 } from "uuid";
import { useSelector } from "react-redux";
import InputBox from "../Components/InputBox";

export default function ScheduledMaintenance({ item, id }) {
  const currentUser = useSelector((state) => state.userObject);
  const [comments, setComments] = useState(item.comments || []);
  const [newComment, setNewComment] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [assignedId, setAssignedId] = useState(v4());
  const [completionData, setCompletionData] = useState({
    actualCost: "",
    actualSupplier: item.supplier || "",
  });
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [activeTab, setActiveTab] = useState("current");

  const documentTypes = ["Completion Report", "Photos", "Invoice", "Other"];

  useEffect(() => {
    const fetchMaintenanceHistory = async () => {
      try {
        setIsLoadingHistory(true);
        const historyData = await queryAllDocsByField(
          "completedMaintenance",
          "maintenanceId",
          "==",
          id
        );
        setMaintenanceHistory(historyData || []);
      } catch (error) {
        console.error("Error fetching maintenance history:", error);
        toast.error("Failed to load maintenance history");
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchMaintenanceHistory();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewComment(value);
  };

  const handleCompletionDataChange = (e) => {
    const { name, value } = e.target;
    setCompletionData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setIsProcessing(true);

    const comment = {
      message: newComment,
      createdAt: new Date().toLocaleString(),
      createdBy: currentUser.displayName,
    };

    setComments((prev) => [...prev, comment]);
    await setDocData("maintenanceItems", id, {
      comments: [...comments, comment],
    });
    setIsProcessing(false);
    toast.success("Comment added successfully");
    setAddMode(false);
    setNewComment("");
  };

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress((prev) => ({
      ...prev,
      [documentType]: 0,
    }));

    const fileName = `${documentType}_${Date.now()}_${file.name.replace(
      /[^a-zA-Z0-9.]/g,
      "_"
    )}`;
    const storageRef = ref(storage, `maintenanceItems/${id}/${fileName}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress((prev) => ({
          ...prev,
          [documentType]: progress,
        }));
      },
      (error) => {
        toast.error(`Error uploading ${documentType}`);
        setUploading(false);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const newDocument = {
            type: documentType,
            name: file.name,
            url: downloadURL,
            uploadedAt: new Date().toLocaleString(),
            uploadedBy: currentUser.displayName,
          };

          setDocuments((prev) => [...prev, newDocument]);
          toast.success(`${documentType} uploaded successfully`);
          setUploadProgress((prev) => ({
            ...prev,
            [documentType]: 100,
          }));
        } catch (error) {
          toast.error("Error getting download URL");
        } finally {
          setUploading(false);
        }
      }
    );
  };

  const handleComplete = async () => {
    setIsProcessing(true);

    // Validation checks
    if (!completionData.actualCost) {
      toast.error("Please enter the actual maintenance cost");
      setIsProcessing(false);
      return;
    }

    if (!completionData.actualSupplier) {
      toast.error("Please enter the supplier name");
      setIsProcessing(false);
      return;
    }

    if (
      window.confirm("Are you sure you want to mark this item as completed?")
    ) {
      const completedObject = {
        ...item,
        maintenanceId: id,
        status: "Completed",
        comments,
        documents,
        actualCost: completionData.actualCost,
        actualSupplier: completionData.actualSupplier,
        completedAt: new Date().toLocaleString(),
        completedBy: currentUser.uid,
        completedName: currentUser.displayName,
        completedEmail: currentUser.email,
      };
      await setDocData("maintenanceItems", id, {
        status: "Updated",
        lastMaintenanceSchedule: new Date().toLocaleString(),
        lastMaintenanceCost: completionData.actualCost,
        lastMaintenanceSupplier: completionData.actualSupplier,
        nextSchedule: "Not Set",
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        updatedEmail: currentUser.email,
        updatedAt: new Date().toLocaleString(),
        updateType: "Maintenance Completion",
      });

      await setDocData("completedMaintenance", assignedId, completedObject);
      setIsProcessing(false);
      toast.success("Maintenance item marked as completed");
    } else {
      return setIsProcessing(false);
    }
  };

  return (
    <div className="w-full">
      {/* Maintenance Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Scheduled Date
          </div>
          <div className="font-medium text-[#363062] flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1.5 text-[#363062]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {item.nextSchedule}
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Supplier Assigned
          </div>
          <div className="font-medium text-[#363062] flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1.5 text-[#363062]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            {item.supplier}
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Budget
          </div>
          <div className="font-medium text-[#363062] flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1.5 text-[#363062]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {item.budget}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex mb-6 border-b border-gray-200">
        <button
          className={`px-4 py-2 text-sm font-medium flex items-center ${
            activeTab === "current"
              ? "text-[#363062] border-b-2 border-[#8FCD00]"
              : "text-gray-500 hover:text-[#363062]"
          }`}
          onClick={() => setActiveTab("current")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Current Maintenance
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium flex items-center ${
            activeTab === "history"
              ? "text-[#363062] border-b-2 border-[#8FCD00]"
              : "text-gray-500 hover:text-[#363062]"
          }`}
          onClick={() => setActiveTab("history")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Maintenance History
        </button>
      </div>

      {activeTab === "current" ? (
        <>
          {/* Comments Section */}
          <div className="mt-6 border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-[#363062] mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-[#363062]">
                  Comments
                </h3>
              </div>
              <button
                className="text-xs flex items-center px-3 py-1 border border-[#363062] text-[#363062] hover:bg-[#363062] hover:text-white rounded transition-all duration-200"
                onClick={() => setAddMode(!addMode)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                {addMode ? "Cancel" : "Add Comment"}
              </button>
            </div>

            {addMode && (
              <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                <textarea
                  className="w-full p-3 text-sm focus:outline-none resize-none border-b border-gray-200"
                  name="comment"
                  placeholder="Type your comment here..."
                  onChange={handleChange}
                  value={newComment}
                  rows={4}
                  required
                ></textarea>
                <div className="flex justify-end p-2 bg-gray-50">
                  <button
                    className="px-3 py-1.5 bg-[#8FCD00] hover:bg-opacity-90 text-white text-xs rounded-lg transition-all duration-200 flex items-center"
                    onClick={handleSubmit}
                    disabled={isProcessing || !newComment.trim()}
                  >
                    {isProcessing ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-3 w-3 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3.5 w-3.5 mr-1.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Submit Comment
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="mt-2 space-y-3">
              {comments && comments.length > 0 ? (
                comments.map((comment, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-3 text-sm"
                  >
                    <div className="flex justify-between items-center mb-2 pb-1 border-b border-gray-100">
                      <span className="font-medium text-[#363062]">
                        {comment.createdBy}
                      </span>
                      <span className="text-xs text-gray-500">
                        {comment.createdAt}
                      </span>
                    </div>
                    <div className="text-gray-700">{comment.message}</div>
                  </div>
                ))
              ) : (
                <div className="text-center p-4 border border-dashed border-gray-200 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-gray-300 mx-auto mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  <p className="text-gray-500 text-sm">No comments yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Completion Section */}
          <div className="mt-6 border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-[#363062] mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="text-sm font-semibold text-[#363062]">
                  Completion Details
                </h3>
              </div>
              <button
                className="text-xs flex items-center px-3 py-1 border border-[#363062] text-[#363062] hover:bg-[#363062] hover:text-white rounded transition-all duration-200"
                onClick={() => setShowCompletionForm(!showCompletionForm)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={
                      showCompletionForm
                        ? "M6 18L18 6M6 6l12 12"
                        : "M12 6v6m0 0v6m0-6h6m-6 0H6"
                    }
                  />
                </svg>
                {showCompletionForm ? "Close" : "Add Completion Details"}
              </button>
            </div>

            {showCompletionForm && (
              <div className="space-y-4">
                {/* Actual Cost and Supplier Input */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <InputBox
                      name="actualCost"
                      label="Actual Maintenance Cost"
                      type="number"
                      placeholder="Enter actual cost"
                      value={completionData.actualCost}
                      onChange={handleCompletionDataChange}
                      required
                    />
                  </div>
                  <div>
                    <InputBox
                      name="actualSupplier"
                      label="Actual Supplier"
                      type="text"
                      placeholder="Enter supplier name"
                      value={completionData.actualSupplier}
                      onChange={handleCompletionDataChange}
                      required
                    />
                  </div>
                </div>

                {/* Document Upload Section */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-[#363062] mb-3">
                    Upload Documents
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {documentTypes.map((type) => (
                      <div
                        key={type}
                        className="border border-gray-200 rounded-lg p-3"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium">{type}</span>
                          {uploadProgress[type] === 100 && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full">
                              Uploaded
                            </span>
                          )}
                        </div>

                        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-all">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            {uploadProgress[type] &&
                            uploadProgress[type] < 100 ? (
                              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                <div
                                  className="bg-[#8FCD00] h-2.5 rounded-full"
                                  style={{ width: `${uploadProgress[type]}%` }}
                                ></div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Uploading: {uploadProgress[type]}%
                                </p>
                              </div>
                            ) : (
                              <>
                                <svg
                                  className="w-8 h-8 mb-1 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                  ></path>
                                </svg>
                                <p className="text-xs text-gray-500">
                                  {uploadProgress[type] === 100
                                    ? "Upload another file"
                                    : "Click to upload"}
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, type)}
                            disabled={uploading && uploadProgress[type] < 100}
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Uploaded Documents List */}
                {documents.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-[#363062] mb-2">
                      Uploaded Documents
                    </h4>
                    <div className="border border-gray-200 rounded-lg divide-y">
                      {documents.map((doc, index) => (
                        <div
                          key={index}
                          className="p-3 flex justify-between items-center"
                        >
                          <div>
                            <span className="text-sm font-medium">
                              {doc.type}
                            </span>
                            <p className="text-xs text-gray-500">{doc.name}</p>
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="mt-6 flex justify-end">
            <button
              className="px-4 py-2 bg-[#363062] hover:bg-opacity-90 text-white rounded-lg text-sm transition-all duration-200 flex items-center"
              onClick={handleComplete}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Mark as Completed
                </>
              )}
            </button>
          </div>
        </>
      ) : (
        <div className="border border-gray-200 rounded-lg p-5">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-[#363062] mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-sm font-semibold text-[#363062]">
              Maintenance History
            </h3>
          </div>

          {isLoadingHistory ? (
            <div className="flex justify-center items-center p-10">
              <svg
                className="animate-spin h-8 w-8 text-[#363062]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : maintenanceHistory.length > 0 ? (
            <div className="space-y-5">
              {maintenanceHistory.map((record, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-center pb-3 mb-3 border-b border-gray-100">
                    <div className="flex items-center">
                      <span className="px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        Completed
                      </span>
                      <span className="ml-3 text-sm text-gray-700">
                        {record.completedAt}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      by {record.completedName}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        Actual Cost
                      </div>
                      <div className="font-medium text-gray-800 flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1.5 text-gray-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {record.actualCost || record.budget || "N/A"}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        Supplier
                      </div>
                      <div className="font-medium text-gray-800 flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1.5 text-gray-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        {record.actualSupplier || record.supplier || "N/A"}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        Date
                      </div>
                      <div className="font-medium text-gray-800 flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1.5 text-gray-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {record.completedAt}
                      </div>
                    </div>
                  </div>

                  {/* Documents */}
                  {record.documents && record.documents.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                        Documents
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {record.documents.map((doc, idx) => (
                          <div
                            key={idx}
                            className="border rounded-lg p-2 flex justify-between items-center"
                          >
                            <div>
                              <span className="text-xs font-medium text-gray-700">
                                {doc.type}
                              </span>
                              <p className="text-xs text-gray-500 truncate w-32">
                                {doc.name}
                              </p>
                            </div>
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              View
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Comments */}
                  {record.comments && record.comments.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                        Comments
                      </div>
                      <div className="space-y-2">
                        {record.comments.slice(0, 2).map((comment, idx) => (
                          <div
                            key={idx}
                            className="text-xs text-gray-700 border-l-2 border-gray-200 pl-3 py-1"
                          >
                            <div className="flex justify-between">
                              <span className="font-medium">
                                {comment.createdBy}
                              </span>
                              <span className="text-gray-500">
                                {comment.createdAt}
                              </span>
                            </div>
                            <p className="mt-1">{comment.message}</p>
                          </div>
                        ))}
                        {record.comments.length > 2 && (
                          <div className="text-xs text-blue-600 hover:underline cursor-pointer">
                            + {record.comments.length - 2} more comments
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-10 border border-dashed border-gray-200 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-gray-300 mx-auto mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h4 className="text-sm font-medium text-gray-700 mb-1">
                No Maintenance History
              </h4>
              <p className="text-xs text-gray-500">
                This item has no previous maintenance records
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
